/**
 * Simulation Page - Fullscreen simulation view with pan and zoom
 * Requirements: 3.1, 3.2, 3.5, 3.6, 3.7
 */

import type { SimulationConfig, SimulationState, Position } from '@langton/shared';
import { deserializeState, type SerializedState, SimulationEngine, createGridNavigator } from '@langton/shared';
import { createRenderer } from '../renderers/index.js';
import type { IRenderer, RenderOptions } from '@langton/shared';
import { WebSocketClient } from '../websocket/websocket-client.js';
import { MusicPlayer } from '../components/music-player.js';

/**
 * Zoom limits as per Requirements 3.6, 3.7
 */
const DEFAULT_MIN_ZOOM = 0.02;  // Default minimum (for large grids)
const MAX_ZOOM = 5.0;  // Ant occupies at most 1/5 of screen (Req 3.7)
const ZOOM_FACTOR = 1.1;  // Multiplicative zoom factor (10% per scroll)

/**
 * Calculate dynamic minimum zoom based on grid size
 * Allows zooming out to see ~4x the grid size
 */
function calculateMinZoom(gridWidth: number, gridHeight: number): number {
  const maxDimension = Math.max(gridWidth, gridHeight);
  // For a 100x100 grid, allow zoom out to 0.25 (4x)
  // For a 1000x1000 grid, allow zoom out to 0.025 (40x)
  // Formula: minZoom = 25 / maxDimension (capped at DEFAULT_MIN_ZOOM)
  const dynamicMin = 25 / maxDimension;
  return Math.max(DEFAULT_MIN_ZOOM, dynamicMin);
}

/**
 * SimulationPage - Fullscreen simulation view
 * 
 * Requirements:
 * - 3.1: Display grid fullscreen
 * - 3.2: Pan view with drag
 * - 3.5: Zoom with scroll wheel
 * - 3.6: Zoom out limit (5x grid size)
 * - 3.7: Zoom in limit (ant 1/5 of screen)
 */
export class SimulationPage {
  private container: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private renderer: IRenderer;
  private wsClient: WebSocketClient;
  private musicPlayer: MusicPlayer;
  
  private config: SimulationConfig | null = null;
  private state: SimulationState | null = null;
  private localEngine: SimulationEngine | null = null;
  
  // View state
  private offset: Position = { x: 0, y: 0 };
  private zoom = 1.0;
  private minZoom = DEFAULT_MIN_ZOOM;  // Dynamic based on grid size
  private cellSize = 20;
  
  // Drag state
  private isDragging = false;
  private dragStart: Position = { x: 0, y: 0 };
  private offsetStart: Position = { x: 0, y: 0 };
  
  // Animation
  private animationFrameId: number | null = null;
  private lastStepTime = 0;
  private simulationId: string;

  constructor(container: HTMLElement, simulationId: string, wsUrl: string) {
    this.container = container;
    this.simulationId = simulationId;
    this.wsClient = new WebSocketClient(wsUrl);
    this.musicPlayer = new MusicPlayer();
    
    // Create fullscreen canvas (Req 3.1)
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d')!;
    this.renderer = createRenderer('square' as any); // Will be updated when config arrives
    
    this.setupUI();
    this.setupEventListeners();
    this.setupWebSocket();
  }

  /**
   * Set up fullscreen UI
   * Requirements: 3.1
   */
  private setupUI(): void {
    this.container.innerHTML = '';
    this.container.style.cssText = `
      background-color: #000000;
      width: 100vw;
      height: 100vh;
      overflow: hidden;
      margin: 0;
      padding: 0;
      position: relative;
    `;

    this.canvas.style.cssText = `
      display: block;
      width: 100%;
      height: 100%;
    `;

    this.container.appendChild(this.canvas);
    this.resizeCanvas();
  }

  /**
   * Resize canvas to match container
   */
  private resizeCanvas(): void {
    this.canvas.width = this.container.clientWidth;
    this.canvas.height = this.container.clientHeight;
    this.render();
  }

  /**
   * Set up event listeners for pan and zoom
   * Requirements: 3.2, 3.5
   */
  private setupEventListeners(): void {
    // Resize handler
    window.addEventListener('resize', () => this.resizeCanvas());

    // Pan with drag (Req 3.2)
    this.canvas.addEventListener('mousedown', (e) => this.onMouseDown(e));
    this.canvas.addEventListener('mousemove', (e) => this.onMouseMove(e));
    this.canvas.addEventListener('mouseup', () => this.onMouseUp());
    this.canvas.addEventListener('mouseleave', () => this.onMouseUp());

    // Zoom with scroll (Req 3.5)
    this.canvas.addEventListener('wheel', (e) => this.onWheel(e));

    // Touch support for mobile
    this.canvas.addEventListener('touchstart', (e) => this.onTouchStart(e));
    this.canvas.addEventListener('touchmove', (e) => this.onTouchMove(e));
    this.canvas.addEventListener('touchend', () => this.onMouseUp());
  }

  /**
   * Handle mouse down for drag start
   */
  private onMouseDown(e: MouseEvent): void {
    this.isDragging = true;
    this.dragStart = { x: e.clientX, y: e.clientY };
    this.offsetStart = { ...this.offset };
    this.canvas.style.cursor = 'grabbing';
  }

  /**
   * Handle mouse move for dragging
   * Requirements: 3.2
   */
  private onMouseMove(e: MouseEvent): void {
    if (!this.isDragging) return;

    const dx = e.clientX - this.dragStart.x;
    const dy = e.clientY - this.dragStart.y;

    this.offset = {
      x: this.offsetStart.x + dx,
      y: this.offsetStart.y + dy
    };

    this.render();
  }

  /**
   * Handle mouse up to end drag
   */
  private onMouseUp(): void {
    this.isDragging = false;
    this.canvas.style.cursor = 'grab';
  }

  /**
   * Handle touch start
   */
  private onTouchStart(e: TouchEvent): void {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      this.isDragging = true;
      this.dragStart = { x: touch.clientX, y: touch.clientY };
      this.offsetStart = { ...this.offset };
    }
  }

  /**
   * Handle touch move
   */
  private onTouchMove(e: TouchEvent): void {
    if (!this.isDragging || e.touches.length !== 1) return;
    e.preventDefault();

    const touch = e.touches[0];
    const dx = touch.clientX - this.dragStart.x;
    const dy = touch.clientY - this.dragStart.y;

    this.offset = {
      x: this.offsetStart.x + dx,
      y: this.offsetStart.y + dy
    };

    this.render();
  }

  /**
   * Handle scroll wheel for zoom
   * Requirements: 3.5, 3.6, 3.7
   */
  private onWheel(e: WheelEvent): void {
    e.preventDefault();

    const oldZoom = this.zoom;
    
    // Multiplicative zoom for smooth scaling at all levels
    const factor = e.deltaY > 0 ? 1 / ZOOM_FACTOR : ZOOM_FACTOR;
    this.zoom = clampZoom(this.zoom * factor, this.minZoom, MAX_ZOOM);

    // Zoom towards mouse position
    if (this.zoom !== oldZoom) {
      const rect = this.canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Adjust offset to zoom towards mouse
      const scale = this.zoom / oldZoom;
      this.offset = {
        x: mouseX - (mouseX - this.offset.x) * scale,
        y: mouseY - (mouseY - this.offset.y) * scale
      };
    }

    this.render();
  }

  /**
   * Set up WebSocket connection
   */
  private setupWebSocket(): void {
    this.wsClient.onInit((simId, serializedState, config) => {
      if (simId === this.simulationId) {
        this.initializeSimulation(serializedState, config);
      }
    });

    this.wsClient.onSync((simId, serializedState, config) => {
      if (simId === this.simulationId) {
        this.syncState(serializedState, config);
      }
    });

    this.wsClient.connect();
    this.wsClient.subscribe(this.simulationId);
  }

  /**
   * Initialize simulation with received state
   */
  private async initializeSimulation(serializedState: SerializedState, config: SimulationConfig): Promise<void> {
    this.config = config;
    this.state = deserializeState(serializedState);
    this.renderer = createRenderer(config.gridType);
    
    // Calculate dynamic min zoom based on grid size
    this.minZoom = calculateMinZoom(config.width, config.height);
    
    // Load render quality setting from server
    try {
      const response = await fetch('/api/simulations/settings/render-quality');
      if (response.ok) {
        const data = await response.json();
        if (this.renderer.setOffscreenCellSize && data.quality) {
          this.renderer.setOffscreenCellSize(data.quality);
        }
      }
    } catch (error) {
      console.error('Failed to load render quality:', error);
    }
    
    // Load avatar image if configured
    if (config.avatarUrl && 'loadAvatarImage' in this.renderer) {
      await (this.renderer as any).loadAvatarImage(config.avatarUrl);
    }
    
    // Create local simulation engine for smooth animation
    const navigator = createGridNavigator(
      config.gridType,
      config.topology,
      config.width,
      config.height
    );
    this.localEngine = new SimulationEngine(config, navigator);
    this.localEngine.setState(this.state);

    // Center view on ant
    this.centerOnAnt();

    // Start music if configured (Req 3.8)
    if (config.musicUrl) {
      this.musicPlayer.mount(this.container, config.musicUrl);
    }

    // Start animation loop
    this.startAnimation();
  }

  /**
   * Sync state from server
   */
  private syncState(serializedState: SerializedState, config: SimulationConfig): void {
    if (!this.localEngine) return;

    const serverState = deserializeState(serializedState);
    
    // Update local engine with server state to prevent drift
    this.localEngine.setState(serverState);
    this.state = serverState;
    this.config = config;
  }

  /**
   * Center view on ant position
   */
  private centerOnAnt(): void {
    if (!this.state || !this.config) return;

    const effectiveCellSize = this.cellSize * this.zoom;
    const antPixelX = this.state.ant.position.x * effectiveCellSize + effectiveCellSize / 2;
    const antPixelY = this.state.ant.position.y * effectiveCellSize + effectiveCellSize / 2;

    this.offset = {
      x: this.canvas.width / 2 - antPixelX,
      y: this.canvas.height / 2 - antPixelY
    };
  }

  /**
   * Start animation loop
   */
  private startAnimation(): void {
    const animate = (timestamp: number) => {
      if (!this.config || !this.localEngine) return;

      // Calculate steps to execute based on speed
      const stepInterval = 1000 / this.config.speed;
      const elapsed = timestamp - this.lastStepTime;

      if (elapsed >= stepInterval) {
        const stepsToExecute = Math.floor(elapsed / stepInterval);
        for (let i = 0; i < stepsToExecute; i++) {
          this.localEngine.step();
        }
        this.state = this.localEngine.getState();
        this.lastStepTime = timestamp - (elapsed % stepInterval);
      }

      this.render();
      this.animationFrameId = requestAnimationFrame(animate);
    };

    this.lastStepTime = performance.now();
    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Render the simulation
   */
  private render(): void {
    if (!this.state || !this.config) {
      // Show loading state
      this.ctx.fillStyle = '#000000';
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.fillStyle = '#FFFFFF';
      this.ctx.font = '24px Arial';
      this.ctx.textAlign = 'center';
      this.ctx.fillText('Загрузка...', this.canvas.width / 2, this.canvas.height / 2);
      return;
    }

    const options: RenderOptions = {
      canvas: this.canvas,
      cellSize: this.cellSize,
      offset: this.offset,
      zoom: this.zoom
    };

    this.renderer.render(this.state, this.config, options);
  }

  /**
   * Get current zoom level
   */
  getZoom(): number {
    return this.zoom;
  }

  /**
   * Set zoom level with clamping
   */
  setZoom(zoom: number): void {
    this.zoom = clampZoom(zoom, this.minZoom, MAX_ZOOM);
    this.render();
  }

  /**
   * Get current offset
   */
  getOffset(): Position {
    return { ...this.offset };
  }

  /**
   * Set offset (for pan)
   */
  setOffset(offset: Position): void {
    this.offset = { ...offset };
    this.render();
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.wsClient.disconnect();
    this.musicPlayer.destroy();
    window.removeEventListener('resize', () => this.resizeCanvas());
  }
}

/**
 * Clamp zoom value within limits
 * Used for Property 14 testing
 * 
 * Requirements: 3.6, 3.7
 * - min: Dynamic based on grid size (allows ~4x zoom out)
 * - MAX_ZOOM (5.0): Ant occupies at most 1/5 of screen
 */
export function clampZoom(zoom: number, min: number = DEFAULT_MIN_ZOOM, max: number = MAX_ZOOM): number {
  return Math.max(min, Math.min(max, zoom));
}

/**
 * Apply pan offset to current offset
 * Used for Property 11 testing
 * 
 * @param currentOffset - Current view offset
 * @param dragVector - Drag movement vector
 * @returns New offset after applying drag
 */
export function applyPanOffset(currentOffset: Position, dragVector: Position): Position {
  return {
    x: currentOffset.x + dragVector.x,
    y: currentOffset.y + dragVector.y
  };
}

// Export zoom limits for testing
export { DEFAULT_MIN_ZOOM as MIN_ZOOM, MAX_ZOOM };
